import * as R from 'ramda';
import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router';
import { useTitle } from 'react-use';
import type { Dispatch } from 'redux';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnmReasonChangeType } from 'APIPackage/CatalogsModule/interfaces/IKnmReasonChangeType';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmPublishStatusesEnum } from 'APIPackage/KnmModule/enums/KnmPublishStatusesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmAttachmentDownload } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { IKnmDocumentRequest } from 'APIPackage/KnmModule/interfaces/IKnmDocumentRequest';
import { IKnmParams } from 'APIPackage/KnmModule/interfaces/IKnmParams';
import { formatDateLocale, getFormatedClientTime } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  ROUTE_ANALYTICS_KNM_ID,
  ROUTE_ANALYTICS_PM_ID,
  ROUTE_KNM_ID,
  ROUTE_PM,
  ROUTE_PM_ID,
} from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { convertKnmErknmFormValuesToCreateParams } from 'KnmErknmPackage/MainModule/common/convertKnmErknmFormValuesToCreateParams';
import { convertKnmErpFormValuesToCreateParams } from 'KnmErpPackage/MainModule/common/convertKnmErpFormValuesToCreateParams';
import { convertKnmPmFormValuesToCreateParams } from 'KnmPmPackage/MainModule/common/convertKnmPmFormValuesToCreateParams';
import { planListGetPlanById } from 'PlanListPackage/MainModule/actions/planListActions';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import {
  getPlanListApprovedForCurrentYear,
  getPlanListPlan,
} from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import {
  setDirtyActGuid,
  setDirtyResultDecisionGuid,
  setInitHash,
} from 'CorePackage/HeaderModule/actions/headerActions';

import { addNoticeHavePlanToChoose } from '../../../CorePackage/NotificationModule/common/addNoticeHavePlanToChoose';
import { addNoticeObjectRiskCategory } from '../../../CorePackage/NotificationModule/common/addNoticeObjectRiskCategory';
import {
  knmAddVisibleModal,
  knmCatalogsInit,
  knmCreate,
  knmCreatePlannedKnm,
  knmGetHistoryChanges,
  knmPlanGetNpa,
  knmPlanGetPunishmentNature,
  knmRemoveVisibleModal,
  knmReset,
  knmSetApproveAddNotificationFunction,
  knmSetDownloadProsecutorDocs,
  knmSetEndByErknm,
  knmSetExternalParams,
  knmSetPath,
  knmSetPublishedStatus,
  knmSetPublishKnm,
  knmSetStatus,
  knmSetStatusText,
  knmSetUnpublishKnm,
  knmSetUrlId,
  knmUpdate,
  knmSetOnAdminSaveButtonClick,
  knmAdminUpdate,
} from '../actions/knmActions';
import { mergeKnmParams } from '../common/mergeKnmParams';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { useKnmDocumentAttachment } from '../hooks/useKnmDocumentAttachment';
import { useKnmPublishById } from '../hooks/useKnmPublishById';
import { IKnmForm } from '../interfaces/IKnmForm';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import {
  getApproveAddNotificationFunction,
  getKnmData,
  getKnmFormRef,
  getKnmIsChangePlanReasonActive,
  getKnmPlanFindDataStatus,
  getKnmPlanFindDataYear,
  getKnmStatus,
  getKnmSystemType,
  getKnmUpdateIsSuccess,
} from '../selectors/knmSelectors';
import { KnmForm } from './KnmForm';
import { useKnmEndErknmById } from '../hooks/useKnmEndErknmById';
import { getLawInterface } from '../../../CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';
import { addNoticeObjectDangerClass } from '../../../CorePackage/NotificationModule/common/addNoticeObjectDangerClass';
import { ErknmPlanStatusesEnum } from '../../../APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { validateAdminSubjectsObjects } from '../common/validateAdminSubjectsObjects';

const convertFormValuesByTypeMap: Record<
  KnmSystemTypesEnum,
  (values: IKnmForm, knmData?: IKnm, isNeededGetDocumentsFromKnmData?: boolean, isDraft?: boolean) => IKnmParams | null
> = {
  [KnmSystemTypesEnum.ERP]: convertKnmErpFormValuesToCreateParams,
  [KnmSystemTypesEnum.ERKNM]: convertKnmErknmFormValuesToCreateParams,
  [KnmSystemTypesEnum.PM]: convertKnmPmFormValuesToCreateParams,
};

interface IKnmStoreProps {
  knmPlan: IKnmPlan | null;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  knmPlanYear: number | undefined;
  status: IKnmStatus | undefined;
  knmRawData: IKnm | undefined;
  isKnmUpdateSuccess: boolean;
  knmSystemType: KnmSystemTypesEnum | undefined;
  plansForSelectInput: number[];
  formRef: IKnmFormRef | undefined;
  approveAddNotification: ((v: boolean) => void) | undefined;
  lawInterface: LawInterfacesEnum;
  isChangePlanReasonActive: boolean;
}

const KnmWithoutStore = ({
  isKnmUpdateSuccess,
  knmPlan,
  knmPlanStatus,
  knmPlanYear,
  knmRawData,
  status,
  knmSystemType,
  plansForSelectInput,
  formRef,
  approveAddNotification,
  lawInterface,
  isChangePlanReasonActive,
}: IKnmStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const initCatalogs = useCallback(() => dispatch(knmCatalogsInit()), [dispatch]);
  const resetKnmStore = useCallback(() => {
    dispatch(knmReset());
  }, [dispatch]);
  const setPublishedStatus = useCallback(
    (publishedStatus: KnmPublishStatusesEnum | undefined) => dispatch(knmSetPublishedStatus(publishedStatus)),
    [dispatch],
  );
  const setStatus = useCallback((statusValue: IKnmStatus | undefined) => dispatch(knmSetStatus(statusValue)), [
    dispatch,
  ]);
  const setStatusText = useCallback((statusText: string | undefined) => dispatch(knmSetStatusText(statusText)), [
    dispatch,
  ]);

  const setKnmEndByErvk = useCallback((knmEndByErknm: () => void) => dispatch(knmSetEndByErknm(knmEndByErknm)), [
    dispatch,
  ]);

  const setPath = useCallback((path: string) => dispatch(knmSetPath(path)), [dispatch]);
  const setPublishKnm = useCallback((publishKnm: () => void) => dispatch(knmSetPublishKnm(publishKnm)), [dispatch]);
  const setUnpublishKnm = useCallback((unpublishKnm: () => void) => dispatch(knmSetUnpublishKnm(unpublishKnm)), [
    dispatch,
  ]);
  const setDownloadKnmProsecutorDocs = useCallback(
    (downloadKnmProsecutorDocs: () => void) => dispatch(knmSetDownloadProsecutorDocs(downloadKnmProsecutorDocs)),
    [dispatch],
  );
  const getPlanById = useCallback((id: IKnm['planId']) => dispatch(planListGetPlanById(id)), [dispatch]);

  const getNpaByControlKindId = useCallback((id: string) => dispatch(knmPlanGetNpa(id)), [dispatch]);

  const getPunishmentNature = useCallback(() => dispatch(knmPlanGetPunishmentNature()), [dispatch]);

  const fetchKnmUpdate: (data: IKnmParams) => void = useCallback((data: IKnmParams) => dispatch(knmUpdate(data)), [
    dispatch,
  ]);
  const setKnmUrlId: (id: string) => void = useCallback((id: string) => dispatch(knmSetUrlId(id)), [dispatch]);

  const [title, setTitle] = useState('КНМ');

  useTitle(title);
  const match = useRouteMatch([ROUTE_ANALYTICS_KNM_ID, ROUTE_ANALYTICS_PM_ID, ROUTE_KNM_ID, ROUTE_PM_ID]);
  const path = match?.path;

  useEffect(() => {
    if (path) {
      setPath(path);
    }
  }, [path, setPath]);

  useEffect(() => {
    if (window.location.hash) {
      dispatch(setInitHash(window.location.hash));
    }
    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      const externalParams = Object.fromEntries(urlParams);

      if (externalParams) {
        dispatch(knmSetExternalParams(externalParams));
      }
    }
  }, []);

  const { id: knmRouteId } = useParams<{ id: string }>();
  const [canSubmit, setCanSubmit] = useState(true);

  const handleAdminSave = useCallback(
    (changeReason: string) => {
      if (!formRef?.current || !knmRawData?.id || !knmSystemType) {
        return;
      }

      if (validateAdminSubjectsObjects(formRef.current, knmSystemType)) {
        return;
      }

      const { values } = formRef.current.getState();
      const knmRequestValues = convertFormValuesByTypeMap[knmSystemType](values, knmRawData);

      if (knmRequestValues) {
        dispatch(knmAdminUpdate(mergeKnmParams(knmRawData, knmRequestValues), changeReason));
      }
    },
    [dispatch, formRef, knmRawData, knmSystemType],
  );

  useEffect(() => {
    dispatch(knmSetOnAdminSaveButtonClick(handleAdminSave));
  }, [dispatch, handleAdminSave]);

  const { downloadZipKnmAttachment } = useKnmDocumentAttachment();

  useEffect(() => {
    initCatalogs();
    dispatch(setDirtyActGuid(undefined));
    dispatch(setDirtyResultDecisionGuid(undefined));

    return () => {
      resetKnmStore();
    };
  }, [initCatalogs, resetKnmStore]);

  useEffect(() => {
    if (knmRouteId) {
      setKnmUrlId(knmRouteId);
    }
  }, [knmRouteId, setKnmUrlId]);

  const [knmParams, setKnmParams] = useState<IKnmParams | null>(null);
  const [isAddKnmToPlanVisible, setIsAddKnmToPlanVisible] = useState(false);

  useEffect(() => {
    if (knmRawData?.pmErknm && path === ROUTE_KNM_ID) {
      window.history.pushState(null, '', `${ROUTE_PM(knmRawData.id)}${window.location.hash}`);
      setKnmUrlId(knmRawData.id.toString());
      window.location.reload();
    }
  }, [knmRawData, path, setKnmUrlId]);

  const createPlannedKnm = useCallback(async () => {
    dispatch(knmCreatePlannedKnm(knmParams, setKnmParams, setIsAddKnmToPlanVisible));
  }, [dispatch, knmParams]);

  useEffect(() => {
    if (knmRawData) {
      knmRawData.planId && getPlanById(knmRawData.planId);
    }
  }, [knmRawData, getPlanById]);

  const isErknm = lawInterface === LawInterfacesEnum.FZ_248;

  useEffect(() => {
    if (isErknm) {
      getPunishmentNature();
    }
  }, []);

  useEffect(() => {
    if (knmRawData?.knmErknm?.kindControl?.dictVersionId) {
      getNpaByControlKindId(knmRawData?.knmErknm?.kindControl?.dictVersionId);
    }
  }, [knmRawData, getPlanById]);

  useEffect(() => {
    if (knmPlan && knmParams && !knmParams.erpId) {
      createPlannedKnm();
    }
  }, [knmParams, knmPlan, createPlannedKnm]);

  const knmId = knmRawData?.id;
  const knmPublishedStatus = knmRawData?.publishedStatus || undefined;
  const knmStatus = knmRawData?.status;

  useEffect(() => {
    if (knmStatus) {
      setStatus(knmStatus);
    }
  }, [knmStatus, setStatus]);

  useEffect(() => {
    setStatusText(status?.name);
  }, [setStatusText, status?.name]);

  const { publishedStatus, publishKnm: publishKnmById, unpublishKnm: unpublishKnmById } = useKnmPublishById(
    knmSystemType,
    knmPublishedStatus,
  );

  const { knmEndByErvk: knmEndByErvkById } = useKnmEndErknmById();

  useEffect(() => {
    setPublishedStatus(publishedStatus || undefined);
  }, [publishedStatus, setPublishedStatus]);

  useEffect(() => {
    const knmName =
      knmRawData && knmRawData.erpId
        ? `${knmSystemType === KnmSystemTypesEnum.PM ? 'ПМ' : 'КНМ'} ${knmRawData.erpId}${
            knmRawData.planId ? ` / ${knmRawData.planId}` : ''
          } `
        : (knmSystemType === KnmSystemTypesEnum.PM && 'ПМ') || 'КНМ';
    const knmDate = `от ${
      knmRawData?.creationDate
        ? formatDateLocale({
            date: knmRawData?.creationDate,
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_ISO_FNS,
          })
        : getFormatedClientTime(DATE_FORMAT_LONG_MINUTE_FNS)
    }`;
    const newTitle = `${knmName} ${knmDate}`;

    setTitle(newTitle);
  }, [knmRawData, knmSystemType]);

  useEffect(() => {
    if (isKnmUpdateSuccess) {
      dispatch(knmGetHistoryChanges());
    }
  }, [dispatch, isKnmUpdateSuccess]);

  const publishKnm = useCallback(() => typeof knmId === 'number' && publishKnmById(knmId), [knmId, publishKnmById]);

  useEffect(() => {
    setPublishKnm(publishKnm);
  }, [publishKnm, setPublishKnm]);

  const knmEndByErvk = useCallback(() => typeof knmId === 'number' && knmEndByErvkById(knmId), [knmId, publishKnmById]);

  useEffect(() => {
    setKnmEndByErvk(knmEndByErvk);
  }, [knmEndByErvk, setKnmEndByErvk]);

  const unpublishKnm = useCallback(() => typeof knmId === 'number' && unpublishKnmById(knmId), [
    knmId,
    unpublishKnmById,
  ]);

  useEffect(() => {
    setUnpublishKnm(unpublishKnm);
  }, [setUnpublishKnm, unpublishKnm]);

  const downloadKnmProsecutorDocs = useCallback(() => {
    if (knmRawData) {
      const prosecutorDocs = knmRawData.knmErknm?.attachmentsWeb?.map(doc => {
        const { fileName, filePath, fileSize, mimeType, bucketName } = doc;
        const documentPath = `${filePath}/${fileName}`;

        return { fileName, filePath: documentPath, fileSize, mimeType, bucketName };
      });
      const reasonsDocs: IKnmAttachmentDownload[] = [];

      knmRawData.knmErknm?.reasonDocuments?.forEach(reason => {
        const docs = reason?.attachments?.map(attachment => attachment);

        if (docs !== undefined) {
          reasonsDocs.push(...docs);
        }
      });
      const downloadDocs: IKnmAttachmentDownload[] = [];

      prosecutorDocs && downloadDocs.push(...prosecutorDocs);
      downloadDocs.push(...reasonsDocs);
      downloadDocs.length && downloadZipKnmAttachment(downloadDocs);
    }
  }, [downloadZipKnmAttachment, knmRawData]);

  useEffect(() => {
    setDownloadKnmProsecutorDocs(downloadKnmProsecutorDocs);
  }, [downloadKnmProsecutorDocs, setDownloadKnmProsecutorDocs]);

  function isValidKnmForCreatePlan(values: IKnmForm): boolean {
    const { objectsErknm, isCreatePlan } = values;

    if (!isCreatePlan) return true;

    if (objectsErknm?.length) {
      const needCheckWithRiskCategory = objectsErknm?.find(item => !!item.riskCategory?.title);

      if (needCheckWithRiskCategory) {
        if (
          objectsErknm?.filter(
            item =>
              item.riskCategory?.title !== 'чрезвычайно высокий риск' && item.riskCategory?.title !== 'высокий риск',
          ).length
        ) {
          dispatch(addNoticeObjectRiskCategory());

          return false;
        }
      } else if (
        objectsErknm?.filter(
          item =>
            item.dangerClass?.title.indexOf('первый') === -1 &&
            item.dangerClass?.title.indexOf('второй') === -1 &&
            item.dangerClass?.title.indexOf('I класс') === -1 &&
            item.dangerClass?.title.indexOf('II класс') === -1,
        ).length
      ) {
        dispatch(addNoticeObjectDangerClass());

        return false;
      }
    } else {
      return false;
    }

    if (plansForSelectInput.length) {
      dispatch(addNoticeHavePlanToChoose());
      formRef?.current.change('planSelectOption');
      formRef?.current.change('isCreatePlan');

      return false;
    }

    return true;
  }

  function submitPlannedKnm(values: IKnmForm, knmRequestValues: IKnmParams) {
    if (values.isCreatePlan) {
      setIsAddKnmToPlanVisible(true);
    } else {
      getPlanById(knmRequestValues.planId);
    }
    setTimeout(() => setKnmParams(knmRequestValues), 1000);
  }

  const handleApproveAddNotification = useCallback(
    (value: boolean) => {
      approveAddNotification && approveAddNotification(value);
    },
    [approveAddNotification],
  );

  const isApproveAddNotification = useCallback(
    (values: IKnmForm): Promise<boolean> => {
      if (!values.subjectNotifications || !values.subjectNotifications.some(item => item.isNew)) {
        return new Promise<boolean>(resolve => resolve(true));
      }

      return new Promise<boolean>(resolve => {
        const approveFunction = (value: boolean) => {
          dispatch(knmRemoveVisibleModal(KnmModalsEnum.APPROVE_ADD_NOTIFICATION_MODAL));
          resolve(value);
        };

        dispatch(knmSetApproveAddNotificationFunction(approveFunction));
        dispatch(knmAddVisibleModal(KnmModalsEnum.APPROVE_ADD_NOTIFICATION_MODAL));
      });
    },
    [dispatch],
  );

  async function handleFormSubmit(values: IKnmForm) {
    if (!canSubmit || !knmSystemType) {
      return null;
    }

    const knmRequestValues = convertFormValuesByTypeMap[knmSystemType](values, knmRawData);

    if (!knmRequestValues || !isValidKnmForCreatePlan(values) || !(await isApproveAddNotification(values))) {
      return null;
    }

    const isPlannedErknm =
      knmRequestValues?.type?.code === KnmTypeCodesEnum.PP && knmSystemType === KnmSystemTypesEnum.ERKNM;
    const abrogationPm = knmRequestValues?.pmErknm?.abrogation;
    const abrogationKnm = knmRequestValues?.knmErknm?.abrogation;

    if (!isChangePlanReasonActive && knmRequestValues.knmErknm?.changePlanChanges) {
      knmRequestValues.knmErknm.changePlanChanges = null;
    }

    if (!knmRawData?.erpId) {
      if (isPlannedErknm && knmRequestValues.planId) {
        submitPlannedKnm(values, knmRequestValues);
      } else {
        dispatch(knmCreate(knmRequestValues));
      }
    } else if (
      knmSystemType === KnmSystemTypesEnum.PM &&
      !values.acceptCancel &&
      abrogationPm &&
      !R.isEmpty(abrogationPm) &&
      abrogationPm?.reasonId
    ) {
      if (status?.code !== KnmErknmStatusCodesEnum.TYPE_CANCELED) {
        dispatch(knmAddVisibleModal(KnmModalsEnum.KNM_CANCELLED_MODAL));

        return null;
      }

      fetchKnmUpdate(mergeKnmParams(knmRawData, knmRequestValues));
    } else if (
      knmSystemType === KnmSystemTypesEnum.ERKNM &&
      !values.acceptCancel &&
      abrogationKnm &&
      !R.isEmpty(abrogationKnm)
    ) {
      if (
        knmRequestValues?.type?.code === KnmTypeCodesEnum.VP ||
        (knmRequestValues?.type?.code === KnmTypeCodesEnum.PP &&
          abrogationKnm?.reasonId !== '1' &&
          abrogationKnm?.reasonId !== '2')
      ) {
        if (status?.code !== KnmErknmStatusCodesEnum.TYPE_CANCELED) {
          dispatch(knmAddVisibleModal(KnmModalsEnum.KNM_CANCELLED_MODAL));

          return null;
        }

        fetchKnmUpdate(mergeKnmParams(knmRawData, knmRequestValues));
      }
      if (
        knmRequestValues?.type?.code === KnmTypeCodesEnum.PP &&
        (abrogationKnm?.reasonId === '1' || abrogationKnm?.reasonId === '2')
      ) {
        dispatch(knmAddVisibleModal(KnmModalsEnum.KNM_EXCLUDE_MODAL));

        return null;
      }
    } else if (
      knmRequestValues?.type?.code === KnmTypeCodesEnum.PP &&
      knmRawData.planId !== knmRequestValues.planId &&
      knmPlanYear === new Date().getFullYear() &&
      knmPlanStatus === ErknmPlanStatusesEnum.APPROVED
    ) {
      getPlanById(knmRequestValues.planId);
      setKnmParams(knmRequestValues);
      setIsAddKnmToPlanVisible(true);
    } else {
      fetchKnmUpdate(mergeKnmParams(knmRawData, knmRequestValues));
    }
  }

  const handleCloseModal = useCallback(() => {
    setIsAddKnmToPlanVisible(false);
  }, []);

  const handleApplyModal = useCallback(
    (selectedReason: IKnmReasonChangeType, documents: IKnmDocumentRequest) => {
      if (knmParams?.knmErknm) {
        const knmErknmParam = {
          ...knmParams.knmErknm,
          changePlanReason: [
            {
              code: null,
              reasonChangeType: selectedReason,
              guid: null,
              dateCreate: null,
              approve: null,
            },
          ],
        };

        if (knmRawData?.erpId) {
          knmParams &&
            fetchKnmUpdate(
              mergeKnmParams(knmRawData, {
                ...knmParams,
                knmErknm: knmErknmParam,
                documents: [...(knmParams.documents || []), documents],
              }),
            );
        } else {
          dispatch(knmCreate({ ...knmParams, knmErknm: knmErknmParam, documents: [documents] }));
        }

        handleCloseModal();
        setKnmParams(null);
      }
    },
    [dispatch, handleCloseModal, knmParams, knmRawData, fetchKnmUpdate],
  );

  return (
    <KnmForm
      knmData={knmRawData}
      visibleModal={isAddKnmToPlanVisible}
      handleSubmit={handleFormSubmit}
      setCanSubmit={setCanSubmit}
      handleCloseModal={handleCloseModal}
      handleApplyModal={handleApplyModal}
      approveAddNotification={handleApproveAddNotification}
      fetchKnmUpdate={fetchKnmUpdate}
      convertFormValuesByTypeMap={convertFormValuesByTypeMap}
    />
  );
};

const mapStateToProps = (state: IRootState): IKnmStoreProps => ({
  knmPlan: getPlanListPlan(state),
  knmPlanStatus: getKnmPlanFindDataStatus(state),
  knmPlanYear: getKnmPlanFindDataYear(state),
  status: getKnmStatus(state),
  knmRawData: getKnmData(state),
  isKnmUpdateSuccess: getKnmUpdateIsSuccess(state),
  knmSystemType: getKnmSystemType(state),
  plansForSelectInput: getPlanListApprovedForCurrentYear(state),
  formRef: getKnmFormRef(state),
  approveAddNotification: getApproveAddNotificationFunction(state),
  lawInterface: getLawInterface(state),
  isChangePlanReasonActive: getKnmIsChangePlanReasonActive(state) || false,
});

export const Knm = connect(mapStateToProps)(KnmWithoutStore);
